<template>
  <div class="p-6 md:p-10">
    <p class="text-xs uppercase tracking-[0.4em] text-amber-500 font-semibold">Overview</p>
    <h1 class="mt-2 text-3xl font-bold text-gray-900">Welcome back{{ userEmail ? `, ${userEmail}` : '' }}</h1>
    <p class="mt-2 text-gray-600">Manage everything shown on the public website from here.</p>

    <div v-if="isDemo" class="mt-6 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-800 leading-relaxed">
      <span class="font-semibold">Running on temporary local storage.</span>
      Firebase isn't connected yet, so changes made here are saved in this browser only.
      Once Firebase credentials are added, this will switch to the real database automatically —
      nothing else needs to change.
    </div>

    <div class="mt-8 grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
      <router-link
        v-for="card in cards"
        :key="card.path"
        :to="card.path"
        class="group rounded-[24px] border border-gray-100 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
      >
        <div class="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-50 text-amber-600 transition group-hover:bg-amber-100">
          <svg class="h-6 w-6" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" :d="card.icon" />
          </svg>
        </div>
        <h3 class="mt-4 text-lg font-bold text-gray-900">{{ card.title }}</h3>
        <p class="mt-1 text-sm text-gray-500">{{ card.description }}</p>
        <span class="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-amber-600">
          Manage
          <svg class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 12h14M13 5l7 7-7 7" />
          </svg>
        </span>
      </router-link>
    </div>
  </div>
</template>

<script>
import { isUsingDemoAuth } from '../../services/authStore'
import { watchAuthState } from '../../services/authStore'

export default {
  name: 'AdminDashboard',
  data() {
    return {
      isDemo: isUsingDemoAuth(),
      userEmail: '',
      unsubscribe: null,
      cards: [
        {
          path: '/admin/packages',
          title: 'Umrah Packages',
          description: 'Month-wise packages with room-type pricing.',
          icon: 'M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0'
        },
        {
          path: '/admin/hotels',
          title: 'Hotels',
          description: 'Makkah & Madina hotel listings and pricing.',
          icon: 'M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21'
        },
        {
          path: '/admin/services',
          title: 'Services',
          description: 'Individual service tiles (hotel, flights, transport, visa).',
          icon: 'M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z'
        },
        {
          path: '/admin/ziarat',
          title: 'Ziarat Tours',
          description: 'Haramain Ziarat guided tour listings.',
          icon: 'M15 10.5a3 3 0 11-6 0 3 3 0 016 0zM19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z'
        },
        {
          path: '/admin/gallery',
          title: 'Gallery',
          description: 'Photos shown on the Gallery page.',
          icon: 'M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3 12V6.75A2.25 2.25 0 015.25 4.5h13.5A2.25 2.25 0 0121 6.75V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15v-1.5z'
        },
        {
          path: '/admin/reviews',
          title: 'Reviews',
          description: 'Pilgrim testimonials shown on the site.',
          icon: 'M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z'
        },
        {
          path: '/admin/vouchers',
          title: 'Vouchers',
          description: 'Create and print booking vouchers for customers.',
          icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
        },
        {
          path: '/admin/invoices',
          title: 'Invoices',
          description: 'Generate and print invoices to bill customers.',
          icon: 'M9 7h6m-6 4h6m-6 4h4M5 3h14a1 1 0 011 1v16l-3-2-3 2-3-2-3 2-3-2-3 2V4a1 1 0 011-1z'
        },
        {
          path: '/admin/ledger',
          title: 'Ledger',
          description: 'Companies/customers with their ledger and outstanding balance.',
          icon: 'M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5'
        }
      ]
    }
  },
  mounted() {
    this.unsubscribe = watchAuthState(user => {
      this.userEmail = user ? user.email : ''
    })
  },
  beforeUnmount() {
    if (this.unsubscribe) this.unsubscribe()
  }
}
</script>
